import _ from "lodash";
import { Capacitor } from "@capacitor/core";
import { getFunctions, httpsCallable } from "firebase/functions";
import authInstance from "./firestore/auth";
import messagingService from "./firestore/messaging";
import User from "./firestore/user";
import Logger from "../utils/logger";
import { APP_IDENTITY } from "config/app";
import { app } from "config/firebase";

const demoUser = _.memoize(() => new User(APP_IDENTITY.demoUserId));

class Session {
  constructor() {
    this.firebaseUser = null;
    this.authenticatedUser = null;
    this._loadPromise = null; // Singleton promise for load operations

    // Log platform detection on initialization
    Logger.debug(
      "Session initialized - Platform:",
      authInstance.isNativeMobile ? "mobile" : "web"
    );
    Logger.debug("Capacitor platform info:", {
      isNativePlatform: Capacitor.isNativePlatform(),
      platform: Capacitor.getPlatform(),
    });
  }

  authenticationStrictlyMatches(uid) {
    return uid === _.get(this.firebaseUser, "uid");
  }

  /**
   * Clear the user cache and force a fresh load on next call
   */
  clearCache() {
    Logger.debug("Clearing user cache");
    this.authenticatedUser = null;
    this._loadPromise = null;
  }

  get demoUser() {
    return demoUser();
  }

  get inDemoMode() {
    // Check if I'm the user
    if (_.get(this.firebaseUser, "uid") === APP_IDENTITY.demoUserId) {
      return false;
    }
    // If not, do a simple check
    return _.get(this.user, "uid") === APP_IDENTITY.demoUserId;
  }

  // Detect if we're running on a native mobile platform
  get isNativeMobile() {
    return authInstance.isNativeMobile;
  }

  get isSignedIn() {
    return authInstance.isSignedIn;
  }

  async load(loadSnapshot = false) {
    // If a load operation is already in progress, wait for it to complete
    if (this._loadPromise) {
      Logger.debug(
        "Load operation already in progress, waiting for completion"
      );
      return this._loadPromise;
    }

    // If user is already loaded, return immediately
    if (this.authenticatedUser) {
      Logger.debug("User already loaded -- returning cached");
      return Promise.resolve(this.authenticatedUser);
    }

    // Create the singleton promise
    this._loadPromise = this._performLoad(loadSnapshot);

    // Clear the promise reference when it completes (success or failure)
    this._loadPromise.finally(() => {
      this._loadPromise = null;
    });

    return this._loadPromise;
  }

  /**
   * Notify other components that authentication state has changed
   */
  notifyAuthStateChange() {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("authStateChanged"));
    }
  }

  /**
   * Notify other components that user data has been updated optimistically
   */
  notifyUserUpdate() {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("userUpdated"));
    }
  }

  // Supabase third-party auth maps the Firebase token to a Postgres role via its
  // `role` claim; raw Firebase tokens lack it, so without this the request is anon.
  // Existing users are backfilled server-side; this covers new sign-ups: if the
  // token has no role claim, ask the backend to stamp it, then force-refresh so the
  // next token carries it. Idempotent and cheap (no network once the claim exists).
  async _ensureAuthRole() {
    try {
      const result = await this.firebaseUser.getIdTokenResult();
      if (result?.claims?.role === "authenticated") return;
      await httpsCallable(getFunctions(app), "ensure_auth_role")();
      await this.firebaseUser.getIdToken(true); // refresh so the claim is in-token
      Logger.debug("Stamped role=authenticated claim for new user");
    } catch (error) {
      Logger.error("Failed to ensure auth role claim:", error);
    }
  }

  async _performLoad(loadSnapshot = false) {
    // Plain async body -- do NOT wrap in `new Promise(async ...)`: a throw in the
    // catch there escapes the executor and is swallowed, so the load promise never
    // settles and callers (e.g. the tug page) spin forever on "Checking...".
    try {
      Logger.debug("Loading user state");

      // Get current user from auth instance
      const currentUser = await authInstance.getCurrentUser();
      if (currentUser) {
        Logger.debug("User found in auth instance");
        this.firebaseUser = currentUser;
        // Make sure the Firebase token carries role=authenticated BEFORE any
        // Supabase read, or those reads run as anon and 401 (new users).
        await this._ensureAuthRole();
        this.authenticatedUser = new User(currentUser.uid);
        await this.authenticatedUser.load(loadSnapshot);

        // Activate messaging (notification permission prompt + FCM token) in
        // the background. Awaiting it here put a native permission/registration
        // round-trip on the critical path of every first load -- e.g. the tug
        // page's "Checking for active session..." waited on it. It's fire-and-
        // forget: nothing downstream needs it and its errors are non-fatal.
        messagingService.activateMessaging().catch((error) => {
          Logger.error(
            "Failed to activate messaging for existing user session:",
            error
          );
        });

        return this.authenticatedUser;
      }

      // Signed out: anon is barred from every table, so don't attempt a read (it
      // would 403). Hand back an unloaded demo placeholder; AuthGuard redirects
      // signed-out visitors to /login, so this object is only for incidental callers.
      Logger.debug("No authenticated user; returning demo placeholder");
      return this.demoUser;
    } catch (error) {
      // Authenticated-user load failed. Return the placeholder rather than a hung
      // promise so callers settle; the page's own error handling takes over.
      Logger.error("Error loading user state:", error);
      return this.demoUser;
    }
  }

  async SignIn() {
    try {
      Logger.debug("Starting sign in process");

      const result = await authInstance.signIn();

      // Return whether this is a new user (first sign in)
      const user = result.user;
      const isNewUser =
        user &&
        user.metadata &&
        user.metadata.creationTime === user.metadata.lastSignInTime;

      Logger.debug(
        "User sign in result - isNewUser:",
        isNewUser,
        "uid:",
        user?.uid
      );

      // Activate messaging service after successful authentication
      // This will request permissions and get FCM token
      try {
        await messagingService.activateMessaging();
        Logger.debug("Messaging service activated after sign in");
      } catch (error) {
        Logger.error("Failed to activate messaging after sign in:", error);
        // Don't let messaging errors prevent successful sign in
      }

      return isNewUser;
    } catch (error) {
      Logger.error("Sign in error:", error);
      throw error;
    }
  }

  async SignOut() {
    try {
      Logger.debug("Starting sign out process");

      // Get user UID before clearing session state
      const userUid = this.authenticatedUser?.uid;

      // Clean up messaging service BEFORE signing out (while user is still authenticated)
      // This must happen first because we need user permissions to update Firestore
      await messagingService.deleteToken(userUid);

      // Reset messaging service state for next login
      await messagingService.reset();

      // Clear session state and load promise
      this.firebaseUser = null;
      this.authenticatedUser = null;
      this._loadPromise = null;

      // Now sign out from auth (this removes Firebase permissions)
      await authInstance.signOut();

      // Don't load the demo user: anon is barred from every table, so the read
      // would 403. AuthGuard redirects the now-signed-out user to /login.

      // Notify other components that authentication state has changed
      this.notifyAuthStateChange();

      Logger.debug("Sign out successful");
    } catch (error) {
      Logger.error("Sign out error:", error);
      // Even if there's an error, clear the session state
      this.firebaseUser = null;
      this.authenticatedUser = null;
      this._loadPromise = null;
      // Still notify of state change even on error
      this.notifyAuthStateChange();
      throw error;
    }
  }

  get user() {
    return this.authenticatedUser || this.demoUser;
  }
}

const CurrentSession = new Session();
export default CurrentSession;
